import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Plus, Calendar, FileText, DollarSign, Tag, Building, Hash, Trash2, Edit2, CheckCircle, AlertCircle, PieChart } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { API_BASE_URL } from '../../config';
import toast from 'react-hot-toast';

interface ExpenseLine {
    id: string;
    date: string;
    company: string;
    invoiceNumber: string;
    description: string;
    category: string;
    reason: string; // Legacy/Fallback
    taxNumber: string;
    amount: number;
    bankFees: number;
    buyerName: string;
    notes: string;
    createdAt: string;
}

interface CustodySheet {
    id: string;
    custodyNumber: string;
    custodyAmount: number;
    status: 'OPEN' | 'CLOSED';
    notes: string;
    lastModified: string;
}

const CATEGORIES = [
    { id: 'Projects', label: 'مصاريف المشاريع' },
    { id: 'Management', label: 'مصاريف الإدارة' },
    { id: 'Branches', label: 'مصاريف الفروع' },
    { id: 'Logistics', label: 'الخدمات اللوجستية' },
    { id: 'Cars', label: 'حركة الشركة' },
    { id: 'Warehouse', label: 'مصاريف المستودعات' },
    { id: 'Communication', label: 'قسم الاتصال' },
    { id: 'Misc', label: 'مصاريف متنوعة' },
];

const ModernCustodyDetailsScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAppContext();

    const [sheet, setSheet] = useState<CustodySheet | null>(null);
    const [lines, setLines] = useState<ExpenseLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [filterCategory, setFilterCategory] = useState('All');

    // Add Form State
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [category, setCategory] = useState(CATEGORIES[0].id);
    const [company, setCompany] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [taxNumber, setTaxNumber] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [bankFees, setBankFees] = useState('');
    const [buyerName, setBuyerName] = useState('');

    const fetchDetails = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const headers: any = {};
            if (user) {
                headers['X-User-Id'] = user.id;
                headers['X-User-Role'] = user.role;
            }
            const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets/${id}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setSheet(data.sheet);
                setLines(data.lines || []);
            } else {
                toast.error('فشل جلب تفاصيل العهدة');
                navigate('/admin/custodies');
            }
        } catch (error) {
            console.error('Error fetching sheet details:', error);
            toast.error('حدث خطأ أثناء الجلب');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [id, user]);

    const handleAddExpense = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!sheet) return;

        try {
            const headers: any = { 'Content-Type': 'application/json' };
            if (user) {
                headers['X-User-Id'] = user.id;
                headers['X-User-Role'] = user.role;
            }

            const payload = {
                date,
                category,
                company,
                invoiceNumber,
                taxNumber,
                description,
                amount: Number(amount),
                bankFees: bankFees ? Number(bankFees) : 0,
                buyerName: buyerName || user?.username,
                reason: category // Fallback for backward compatibility
            };

            const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets/${sheet.id}/lines`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success('تمت إضافة المصروف بنجاح');
                setShowAddModal(false);
                // Reset form
                setCompany('');
                setInvoiceNumber('');
                setTaxNumber('');
                setDescription('');
                setAmount('');
                setBankFees('');
                fetchDetails();
            } else {
                toast.error('فشل إضافة المصروف');
            }
        } catch (error) {
            console.error('Error adding expense:', error);
            toast.error('حدث خطأ غير متوقع');
        }
    };

    const handleDeleteLine = async (lineId: string) => {
        if (!confirm('هل أنت متأكد من حذف هذا السجل؟')) return;
        try {
            const headers: any = {};
            if (user) {
                headers['X-User-Id'] = user.id;
                headers['X-User-Role'] = user.role;
            }
            const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets/${sheet?.id}/lines/${lineId}`, {
                method: 'DELETE',
                headers
            });
            if (res.ok) {
                toast.success('تم الحذف');
                fetchDetails();
            } else {
                toast.error('فشل الحذف');
            }
        } catch (e) {
            toast.error('خطأ أثناء الحذف');
        }
    };

    // Calculations
    const totalStats = useMemo(() => {
        const totalSpent = lines.reduce((acc, l) => acc + (l.amount || 0) + (l.bankFees || 0), 0);
        const custodyAmount = sheet?.custodyAmount || 0;
        const remaining = custodyAmount - totalSpent;
        const percent = custodyAmount > 0 ? (totalSpent / custodyAmount) * 100 : 0;
        return { totalSpent, remaining, percent };
    }, [lines, sheet]);

    const filteredLines = useMemo(() => {
        if (filterCategory === 'All') return lines;
        return lines.filter(l => (l.category === filterCategory) || (l.reason === filterCategory));
    }, [lines, filterCategory]);

    const categoryBreakdown = useMemo(() => {
        const data: Record<string, number> = {};
        lines.forEach(l => {
            const cat = l.category || l.reason || 'Uncategorized';
            const cost = (l.amount || 0) + (l.bankFees || 0);
            data[cat] = (data[cat] || 0) + cost;
        });
        return data;
    }, [lines]);

    if (loading) return <div className="p-10 text-center text-slate-400">جاري التحميل...</div>;
    if (!sheet) return <div className="p-10 text-center text-red-400">عهدة غير موجودة</div>;

    return (
        <div className="p-6 space-y-6 animate-fade-in-up pb-20">
            {/* Header / Nav */}
            <div className="flex items-center gap-4 text-slate-400 mb-2">
                <button onClick={() => navigate('/admin/custodies')} className="hover:text-white transition-colors flex items-center gap-1">
                    <ArrowRight size={16} />
                    العهد
                </button>
                <span>/</span>
                <span className="text-white font-medium">{sheet.custodyNumber}</span>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">تفاصيل العهدة {sheet.custodyNumber}</h1>
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-xs rounded-full font-bold ${sheet.status === 'OPEN' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                            {sheet.status === 'OPEN' ? 'نشطة' : 'مغلقة'}
                        </span>
                        <span className="text-sm text-slate-400">{sheet.notes}</span>
                    </div>
                </div>
                {sheet.status === 'OPEN' && (
                    <div className="flex gap-3">
                        <button
                            onClick={() => window.print()}
                            className="flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                        >
                            <FileText size={20} />
                            طباعة التقرير
                        </button>
                        <button
                            onClick={async () => {
                                if (!confirm('هل أنت متأكد من إغلاق هذه العهدة؟ لا يمكن إضافة مصروفات بعد الإغلاق.')) return;
                                try {
                                    const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets/${sheet.id}/close`, {
                                        method: 'POST',
                                        headers: user ? { 'X-User-Id': user.id, 'X-User-Role': user.role } : {}
                                    });
                                    if (res.ok) {
                                        toast.success('تم إغلاق العهدة بنجاح');
                                        fetchDetails();
                                    } else {
                                        toast.error('فشل الإغلاق');
                                    }
                                } catch (e) { toast.error('خطأ في الاتصال'); }
                            }}
                            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-rose-600 text-white hover:bg-rose-500 transition-colors shadow-lg shadow-rose-500/20"
                        >
                            <CheckCircle size={20} />
                            إغلاق العهدة
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/20"
                        >
                            <Plus size={20} />
                            تسجيل مصروف جديد
                        </button>
                    </div>
                )}
                {sheet.status === 'CLOSED' && (
                    <button
                        onClick={() => window.print()}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-500/20"
                    >
                        <FileText size={20} />
                        طباعة كشف الإغلاق
                    </button>
                )}
            </div>

            {/* Monitoring Dashboard */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Progress Card */}
                <div className="lg:col-span-2 glass-panel p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <PieChart size={20} className="text-blue-400" />
                        المراقبة اللحظية
                    </h3>

                    <div className="mb-8">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-400">نسبة الاستهلاك</span>
                            <span className={`font-bold ${totalStats.percent > 90 ? 'text-rose-400' : 'text-emerald-400'}`}>
                                {totalStats.percent.toFixed(1)}%
                            </span>
                        </div>
                        <div className="h-4 w-full bg-slate-700/50 rounded-full overflow-hidden backdrop-blur-sm border border-slate-600/30">
                            <div
                                className={`h-full relative overflow-hidden transition-all duration-1000 ease-out ${totalStats.percent > 90 ? 'bg-gradient-to-r from-rose-600 to-rose-400' : 'bg-gradient-to-r from-emerald-600 to-emerald-400'}`}
                                style={{ width: `${Math.min(totalStats.percent, 100)}%` }}
                            >
                                <div className="absolute inset-0 bg-white/20 animate-pulse-slow"></div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/30">
                            <p className="text-xs text-slate-400 mb-1">الرصيد الأساسي</p>
                            <p className="text-xl font-bold text-white">{sheet.custodyAmount.toLocaleString()}</p>
                        </div>
                        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/30">
                            <p className="text-xs text-slate-400 mb-1">إجمالي المنصرف</p>
                            <p className="text-xl font-bold text-rose-400">{totalStats.totalSpent.toLocaleString()}</p>
                        </div>
                        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/30">
                            <p className="text-xs text-slate-400 mb-1">المتبقي</p>
                            <p className="text-xl font-bold text-emerald-400">{totalStats.remaining.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                {/* Category Breakdown */}
                <div className="glass-panel p-6 rounded-2xl max-h-[350px] overflow-y-auto">
                    <h3 className="text-lg font-bold text-white mb-4">ملخص الأقسام</h3>
                    <div className="space-y-3">
                        {Object.entries(categoryBreakdown).sort((a, b) => b[1] - a[1]).map(([catId, val]) => {
                            const catLabel = CATEGORIES.find(c => c.id === catId)?.label || catId;
                            const pct = sheet.custodyAmount > 0 ? (val / sheet.custodyAmount) * 100 : 0;
                            return (
                                <div key={catId} className="flex flex-col gap-1">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-slate-300">{catLabel}</span>
                                        <span className="text-slate-400">{val.toLocaleString()}</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden">
                                        <div className="h-full bg-blue-500/70" style={{ width: `${Math.min(pct, 100)}%` }}></div>
                                    </div>
                                </div>
                            );
                        })}
                        {Object.keys(categoryBreakdown).length === 0 && (
                            <p className="text-sm text-slate-500 text-center py-4">لا توجد مصروفات</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Expenses List */}
            <div className="glass-panel rounded-2xl overflow-hidden border border-slate-700/50">
                <div className="p-6 border-b border-slate-700/50 flex flex-col md:flex-row justify-between items-center gap-4">
                    <h3 className="text-lg font-bold text-white">سجل المصروفات التفصيلي</h3>
                    <div className="flex gap-2">
                        {['All', ...CATEGORIES.map(c => c.id)].map(c => {
                            const label = c === 'All' ? 'الكل' : CATEGORIES.find(cat => cat.id === c)?.label;
                            // Limit tabs or use a select dropdown if too many. Use select for mobile friendliness.
                            return null;
                        })}
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="bg-slate-800 border border-slate-700 text-sm rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                        >
                            <option value="All">كل الأقسام</option>
                            {CATEGORIES.map(c => (
                                <option key={c.id} value={c.id}>{c.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-right">
                        <thead className="bg-slate-800/50 text-slate-400 font-medium">
                            <tr>
                                <th className="px-6 py-4">التاريخ</th>
                                <th className="px-6 py-4">القسم</th>
                                <th className="px-6 py-4">المستفيد</th>
                                <th className="px-6 py-4">الفاتورة</th>
                                <th className="px-6 py-4">البيان</th>
                                <th className="px-6 py-4">المبلغ</th>
                                <th className="px-6 py-4">الرسوم</th>
                                <th className="px-6 py-4">الإجمالي</th>
                                <th className="px-6 py-4">المشتري</th>
                                {sheet.status === 'OPEN' && <th className="px-6 py-4">إجراء</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {filteredLines.length === 0 ? (
                                <tr><td colSpan={10} className="px-6 py-8 text-center text-slate-500">لا توجد بيانات للعرض</td></tr>
                            ) : filteredLines.map((line) => {
                                const catLabel = CATEGORIES.find(c => c.id === line.category)?.label || line.category || line.reason;
                                return (
                                    <tr key={line.id} className="hover:bg-slate-800/30 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-slate-300">{line.date}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-xs border border-slate-700">
                                                {catLabel}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-300">{line.company}</td>
                                        <td className="px-6 py-4 text-slate-400 text-xs">
                                            <div className="flex flex-col">
                                                <span className="font-mono">{line.invoiceNumber}</span>
                                                <span className="text-slate-600">{line.taxNumber}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-300 max-w-xs truncate" title={line.description}>{line.description}</td>
                                        <td className="px-6 py-4 text-white font-medium">{line.amount.toLocaleString()}</td>
                                        <td className="px-6 py-4 text-slate-400">{line.bankFees ? line.bankFees.toLocaleString() : '-'}</td>
                                        <td className="px-6 py-4 text-emerald-400 font-bold">{(line.amount + (line.bankFees || 0)).toLocaleString()}</td>
                                        <td className="px-6 py-4 text-slate-400">{line.buyerName}</td>
                                        {sheet.status === 'OPEN' && (
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleDeleteLine(line.id)}
                                                    className="p-2 text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Expense Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl p-6 shadow-2xl animate-fade-in-up my-10">
                        <div className="flex justify-between items-center mb-6 border-b border-slate-700 pb-4">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <Plus className="text-blue-500" />
                                تسجيل مصروف جديد
                            </h2>
                            <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">✕</button>
                        </div>

                        <form onSubmit={handleAddExpense} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">التاريخ *</label>
                                    <input
                                        type="date"
                                        required
                                        value={date}
                                        onChange={e => setDate(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">القسم/التصنيف *</label>
                                    <select
                                        required
                                        value={category}
                                        onChange={e => setCategory(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                    >
                                        {CATEGORIES.map(c => (
                                            <option key={c.id} value={c.id}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">المستفيد (الشركة) *</label>
                                    <input
                                        type="text"
                                        required
                                        value={company}
                                        onChange={e => setCompany(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500 placeholder-slate-600"
                                        placeholder="اسم المورد/المحل"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">اسم المشتري</label>
                                    <input
                                        type="text"
                                        value={buyerName}
                                        onChange={e => setBuyerName(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500 placeholder-slate-600"
                                        placeholder={user?.username || 'اسم المشتري'}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">رقم الفاتورة</label>
                                    <input
                                        type="text"
                                        value={invoiceNumber}
                                        onChange={e => setInvoiceNumber(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500 placeholder-slate-600"
                                        placeholder="رقم الفاتورة الضريبي"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">الرقم الضريبي للمورد</label>
                                    <input
                                        type="text"
                                        value={taxNumber}
                                        onChange={e => setTaxNumber(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500 placeholder-slate-600"
                                        placeholder="الرقم الضريبي"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm text-slate-400 mb-1">البيان (الوصف) *</label>
                                    <input
                                        type="text"
                                        required
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500 placeholder-slate-600"
                                        placeholder="وصف السلعة أو الخدمة"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">المبلغ الأساسي *</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            required
                                            value={amount}
                                            onChange={e => setAmount(e.target.value)}
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500 text-left ltr"
                                            placeholder="0.00"
                                            step="0.01"
                                        />
                                        <span className="absolute right-3 top-2.5 text-slate-500 text-xs">SAR</span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">رسوم إضافية/بنكية</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={bankFees}
                                            onChange={e => setBankFees(e.target.value)}
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500 text-left ltr"
                                            placeholder="0.00"
                                            step="0.01"
                                        />
                                        <span className="absolute right-3 top-2.5 text-slate-500 text-xs">SAR</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 mt-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-400">الإجمالي النهائي للخصم</span>
                                    <span className="text-xl font-bold text-emerald-400">
                                        {((Number(amount) || 0) + (Number(bankFees) || 0)).toLocaleString()} <span className="text-xs">ر.س</span>
                                    </span>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6 pt-4 border-t border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-6 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                                >
                                    إلغاء
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-6 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20 font-medium"
                                >
                                    إضافة المصروف
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModernCustodyDetailsScreen;
